/**
 * InventoryItemsList Component
 *
 * Displays a table of inventory items with CRUD actions.
 * Supports filtering by category and active/archived status.
 * Shows stock levels and low stock indicators.
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 2: Inventory Items Management
 */

import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useInventoryItems } from '@/hooks/useInventoryItems'
import InventoryItemDialog from './InventoryItemDialog'
import LowStockBadge from './LowStockBadge'
import CategoryTabs, { getCategoryLabel } from './CategoryTabs'
import { toast } from 'sonner'
import {
  Package,
  Plus,
  ChevronDown,
  Droplets,
  Wheat,
  Boxes,
  MoreHorizontal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'

/**
 * Production phases for grouping inventory items
 */
const PRODUCTION_PHASES = {
  liquid_culture: {
    label: 'Liquid Culture',
    icon: Droplets,
    keywords: ['malt', 'agar', 'petri', 'syringe', 'lc ', 'liquid culture', 'honey', 'dextrose', 'media'],
  },
  spawn: {
    label: 'Spawn',
    icon: Wheat,
    keywords: ['spawn', 'grain', 'rye', 'millet', 'oat', 'sorghum', 'wheat grain'],
  },
  substrate: {
    label: 'Substrate',
    icon: Boxes,
    keywords: ['hardwood', 'pellet', 'soy', 'hull', 'gypsum', 'lime', 'satrise', 'xls', 'bran', 'straw', 'substrate', 'sawdust'],
  },
  general: {
    label: 'General',
    icon: MoreHorizontal,
    keywords: [],
  },
}

/**
 * Get production phase from item
 * Uses explicit production_phase field if set, otherwise falls back to keyword inference
 */
function getProductionPhase(item) {
  // Use explicit production_phase if set
  if (item.production_phase && PRODUCTION_PHASES[item.production_phase]) {
    return item.production_phase
  }

  // Fallback to keyword inference for legacy items
  const searchText = `${item.name} ${item.notes || ''}`.toLowerCase()

  for (const [phase, config] of Object.entries(PRODUCTION_PHASES)) {
    if (phase === 'general') continue
    if (config.keywords.some(keyword => searchText.includes(keyword))) {
      return phase
    }
  }

  return 'general'
}

/**
 * Format stock level for display
 * Shows base units and optionally purchase units
 */
function formatStock(item) {
  const stock = item.current_stock ?? 0
  const unit = item.unit_of_measure || 'unit'

  if (item.purchase_unit && item.conversion_factor && item.conversion_factor !== 1) {
    const purchaseQty = stock / item.conversion_factor
    return `${stock.toFixed(1)} ${unit} (${purchaseQty.toFixed(1)} ${item.purchase_unit}s)`
  }

  return `${stock.toFixed(1)} ${unit}`
}

/**
 * Calculate stock percentage for progress bar
 * Based on reorder point (100% = at reorder point, >100% = above)
 */
function getStockPercentage(item) {
  if (!item.reorder_point || item.reorder_point === 0) {
    // If no reorder point, consider any stock as 100%
    return item.current_stock > 0 ? 100 : 0
  }
  // Calculate as percentage of twice the reorder point (so 100% = 2x reorder)
  const percentage = (item.current_stock / (item.reorder_point * 2)) * 100
  return Math.min(Math.max(percentage, 0), 100)
}

/**
 * Get progress bar color based on stock level
 */
function getStockColor(item) {
  if (item.is_low_stock) {
    return 'bg-amber-500'
  }
  return 'bg-green-500'
}

export default function InventoryItemsList() {
  const {
    items,
    // allItems not used in this component, items already filtered by categoryFilter
    isLoading,
    error,
    categoryCounts,
    categoryFilter,
    archiveItem,
    restoreItem,
    refetch,
    filterByCategory,
  } = useInventoryItems()

  const [statusFilter, setStatusFilter] = useState('active')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, item: null, action: null })
  const [sortConfig, setSortConfig] = useState({ key: 'category', direction: 'asc' })

  // Filter items based on active/archived status
  const filteredItems = items.filter(item => {
    if (statusFilter === 'active') return item.active === true
    if (statusFilter === 'archived') return item.active === false
    return true
  })

  // Get counts for status filter
  const activeCount = items.filter(i => i.active).length
  const archivedCount = items.filter(i => !i.active).length

  // Handle column header click for sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  // Group items by production phase
  const groupedItems = useMemo(() => {
    // Category sort order for custom sorting
    const CATEGORY_ORDER = { raw_material: 0, packaging: 1, sundry: 2, other: 3 }

    // Sort function for items
    const sortItems = (items) => {
      return [...items].sort((a, b) => {
        let aVal, bVal

        switch (sortConfig.key) {
          case 'name':
            aVal = a.name.toLowerCase()
            bVal = b.name.toLowerCase()
            break
          case 'batch_prefix':
            aVal = (a.batch_prefix || '').toLowerCase()
            bVal = (b.batch_prefix || '').toLowerCase()
            break
          case 'category':
            aVal = CATEGORY_ORDER[a.category] ?? 4
            bVal = CATEGORY_ORDER[b.category] ?? 4
            break
          case 'unit_of_measure':
            aVal = (a.unit_of_measure || '').toLowerCase()
            bVal = (b.unit_of_measure || '').toLowerCase()
            break
          case 'current_stock':
            aVal = a.current_stock ?? 0
            bVal = b.current_stock ?? 0
            break
          case 'reorder_point':
            aVal = a.reorder_point ?? 0
            bVal = b.reorder_point ?? 0
            break
          case 'supplier':
            aVal = (a.preferred_supplier?.name || '').toLowerCase()
            bVal = (b.preferred_supplier?.name || '').toLowerCase()
            break
          case 'active':
            aVal = a.active ? 0 : 1
            bVal = b.active ? 0 : 1
            break
          default:
            aVal = a.name.toLowerCase()
            bVal = b.name.toLowerCase()
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }

    const groups = {
      liquid_culture: [],
      spawn: [],
      substrate: [],
      general: [],
    }

    filteredItems.forEach(item => {
      const phase = getProductionPhase(item)
      groups[phase].push(item)
    })

    // Sort items within each group using the current sort config
    Object.keys(groups).forEach(key => {
      groups[key] = sortItems(groups[key])
    })

    return groups
  }, [filteredItems, sortConfig])

  // Track expanded state for each group
  const [expandedGroups, setExpandedGroups] = useState({
    liquid_culture: true,
    spawn: true,
    substrate: true,
    general: true,
  })

  const toggleGroup = (phase) => {
    setExpandedGroups(prev => ({ ...prev, [phase]: !prev[phase] }))
  }

  // Sortable header component
  const SortableHeader = ({ sortKey, children, className = '' }) => (
    <TableHead
      className={`cursor-pointer select-none hover:bg-slate-50 ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortConfig.key === sortKey ? (
          sortConfig.direction === 'asc' ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  )

  const handleAddItem = () => {
    setEditingItem(null)
    setDialogOpen(true)
  }

  const handleEditItem = (item) => {
    setEditingItem(item)
    setDialogOpen(true)
  }

  const handleArchiveClick = (item) => {
    setConfirmDialog({ open: true, item, action: 'archive' })
  }

  const handleRestoreClick = (item) => {
    setConfirmDialog({ open: true, item, action: 'restore' })
  }

  const handleConfirmAction = async () => {
    const { item, action } = confirmDialog
    if (!item || !action) return

    try {
      if (action === 'archive') {
        await archiveItem(item.id)
        toast.success('Item archived', {
          description: `${item.name} has been archived.`,
        })
      } else if (action === 'restore') {
        await restoreItem(item.id)
        toast.success('Item restored', {
          description: `${item.name} has been restored.`,
        })
      }
    } catch (err) {
      toast.error('Operation failed', {
        description: err.message,
      })
    } finally {
      setConfirmDialog({ open: false, item: null, action: null })
    }
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setEditingItem(null)
  }

  const handleDialogSuccess = () => {
    setDialogOpen(false)
    setEditingItem(null)
    refetch()
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-red-600 py-8">
            <p className="text-sm font-medium">Error loading inventory items</p>
            <p className="text-xs text-slate-500 mt-1">{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={refetch}>
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-slate-600" />
            <CardTitle className="text-lg font-medium">Inventory Items</CardTitle>
          </div>
          <Button variant="create" size="sm" onClick={handleAddItem}>
            <Plus className="h-4 w-4 mr-1" />
            Add Item
          </Button>
        </CardHeader>
        <CardContent>
          {/* Category Filter Tabs */}
          <div className="mb-4">
            <CategoryTabs
              value={categoryFilter}
              onValueChange={filterByCategory}
              counts={categoryCounts}
            />
          </div>

          {/* Status Filter Tabs */}
          <div className="mb-4">
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList>
                <TabsTrigger value="active">
                  Active ({activeCount})
                </TabsTrigger>
                <TabsTrigger value="archived">
                  Archived ({archivedCount})
                </TabsTrigger>
                <TabsTrigger value="all">
                  All ({items.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Items grouped by production phase */}
          {filteredItems.length === 0 ? (
            <div className="text-center text-slate-500 py-12">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No items found</p>
              <p className="text-xs text-slate-400 mt-1">
                {statusFilter === 'archived'
                  ? 'No archived items'
                  : categoryFilter !== 'all'
                    ? `No items in ${getCategoryLabel(categoryFilter).toLowerCase()} category`
                    : 'Add your first inventory item to get started'}
              </p>
              {statusFilter === 'active' && categoryFilter === 'all' && (
                <Button variant="outline" size="sm" className="mt-4" onClick={handleAddItem}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(PRODUCTION_PHASES).map(([phase, config]) => {
                const phaseItems = groupedItems[phase]
                if (phaseItems.length === 0) return null

                const PhaseIcon = config.icon
                const lowStockCount = phaseItems.filter(i => i.is_low_stock && i.active).length

                return (
                  <Collapsible
                    key={phase}
                    open={expandedGroups[phase]}
                    onOpenChange={() => toggleGroup(phase)}
                  >
                    <CollapsibleTrigger asChild>
                      <button className="flex items-center justify-between w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors">
                        <div className="flex items-center gap-3">
                          <PhaseIcon className="h-5 w-5 text-slate-600" />
                          <span className="font-medium text-slate-900">{config.label}</span>
                          <Badge variant="secondary" className="text-xs">
                            {phaseItems.length} {phaseItems.length === 1 ? 'item' : 'items'}
                          </Badge>
                          {lowStockCount > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {lowStockCount} low stock
                            </Badge>
                          )}
                        </div>
                        <ChevronDown
                          className={`h-5 w-5 text-slate-500 transition-transform ${
                            expandedGroups[phase] ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="rounded-md border mt-2">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <SortableHeader sortKey="name">Name</SortableHeader>
                              <SortableHeader sortKey="batch_prefix">Prefix</SortableHeader>
                              <SortableHeader sortKey="category">Category</SortableHeader>
                              <SortableHeader sortKey="unit_of_measure">Unit</SortableHeader>
                              <SortableHeader sortKey="current_stock">Stock Level</SortableHeader>
                              <SortableHeader sortKey="reorder_point">Reorder Point</SortableHeader>
                              <SortableHeader sortKey="supplier">Supplier</SortableHeader>
                              <SortableHeader sortKey="active" className="text-center">Status</SortableHeader>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {phaseItems.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">
                                  <button
                                    type="button"
                                    onClick={() => handleEditItem(item)}
                                    className="text-left text-slate-900 hover:text-slate-600 hover:underline cursor-pointer flex items-center gap-2"
                                  >
                                    {item.name}
                                    <LowStockBadge show={item.is_low_stock && item.active} />
                                  </button>
                                </TableCell>
                                <TableCell>
                                  <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                                    {item.batch_prefix}
                                  </code>
                                </TableCell>
                                <TableCell className="text-slate-600">
                                  {getCategoryLabel(item.category)}
                                </TableCell>
                                <TableCell className="text-slate-600">
                                  {item.unit_of_measure}
                                  {item.purchase_unit && (
                                    <span className="text-xs text-slate-400 block">
                                      ({item.conversion_factor} per {item.purchase_unit})
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="min-w-[120px]">
                                    <div className="text-sm">{formatStock(item)}</div>
                                    <Progress
                                      value={getStockPercentage(item)}
                                      className="h-1.5 mt-1"
                                      indicatorClassName={getStockColor(item)}
                                    />
                                  </div>
                                </TableCell>
                                <TableCell className="text-slate-600">
                                  {item.reorder_point ?? 0} {item.unit_of_measure}
                                </TableCell>
                                <TableCell className="text-slate-600">
                                  {item.preferred_supplier?.name || '-'}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge
                                    variant="outline"
                                    className={
                                      item.active
                                        ? 'bg-green-50 text-green-700 border-green-200'
                                        : 'bg-slate-50 text-slate-600 border-slate-200'
                                    }
                                  >
                                    {item.active ? 'Active' : 'Archived'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Item Dialog */}
      <InventoryItemDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        item={editingItem}
        onSuccess={handleDialogSuccess}
        onArchive={(item) => {
          handleDialogClose()
          handleArchiveClick(item)
        }}
        onRestore={(item) => {
          handleDialogClose()
          handleRestoreClick(item)
        }}
      />

      {/* Confirm Dialog for Archive/Restore */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, item: null, action: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.action === 'archive' ? 'Archive Item?' : 'Restore Item?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === 'archive'
                ? `This will hide ${confirmDialog.item?.name} from the active items list. You can restore it later if needed.`
                : `This will restore ${confirmDialog.item?.name} to the active items list.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={confirmDialog.action === 'archive' ? '' : 'bg-green-600 hover:bg-green-700'}
            >
              {confirmDialog.action === 'archive' ? 'Archive' : 'Restore'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
